import {
  Button,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { BlockDraftAsyncDomain } from '@strength/domain';
import { MdError } from '@teamworksdev/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '../../../../loco-sync';
import { trackEvent } from '../../../../reporting/reporting';
import { useToast } from '../../../hooks/useToast';
import { isoValueToDisplay } from '../slots/date';
import { BlockGroupBy } from '../types';
import { WorkoutIdWithCycleAndSession } from '../utils';
import { DisabledWithTooltip } from '../../../components/permissions/DisabledWithTooltip';
import { isPublishActionDisabled, PublishState } from './publish-state';

export function PublishChip({
  state,
  groupBy,
  setSessionId,
  setCycleId,
}: {
  state: PublishState;
  groupBy: BlockGroupBy;
  setSessionId: (id: number) => void;
  setCycleId: (id: number) => void;
}) {
  // TODO: Show AMS sync error badge if there are any AMS sync errors
  const { t } = useTranslation();

  if (state.type === 'pending') {
    return (
      <ChipWrapper message={t('publishPending')}>
        <LinearProgress key="pending" />
      </ChipWrapper>
    );
  } else if (state.type === 'in_progress') {
    return (
      <ChipWrapper message={t('publishInProgress')}>
        <LinearProgress
          key="in_progress"
          variant="determinate"
          value={state.value}
        />
      </ChipWrapper>
    );
  } else if (state.type === 'stalled') {
    return (
      <Tooltip
        title={
          <div>
            {t('publishRetryMessage')}
            <br />
            {t('publishSupportMessage')}
          </div>
        }
      >
        <ChipWrapper message={t('publishStalled')}>
          <LinearProgress key="stalled" color="error" />
        </ChipWrapper>
      </Tooltip>
    );
  } else if (state.type === 'send_failed') {
    // SC-5458: the publish message never reached the worker. The publish
    // action stays enabled, so the coach retries by pressing Publish.
    return (
      <Tooltip
        title={
          <div>
            {t('publishRetryMessage')}
            <br />
            {t('publishSupportMessage')}
          </div>
        }
      >
        <ChipWrapper message={t('publishNotStarted')} />
      </Tooltip>
    );
  } else if (state.type === 'error') {
    return (
      <BlockPublishErrorBadge
        workoutIdsWithErrors={state.workoutWithErrors}
        onSelectError={({ blockCycleId, blockSessionId }) => {
          if (groupBy === 'session') {
            setSessionId(blockSessionId);
          } else if (groupBy === 'cycle') {
            setCycleId(blockCycleId);
          }
          // 'all': no-op — error navigation doesn't apply in All view
        }}
      />
    );
  }

  return null;
}

const ChipWrapper = ({
  message,
  children,
}: {
  message: string;
  // Optional: the send_failed chip is message-only (SC-5458).
  children?: React.ReactNode;
}) => (
  <div className="flex flex-col w-[150px] justify-center">
    <Typography variant="body2" className="pb-1">
      {message}
    </Typography>
    {children}
  </div>
);

export function PublishHeader({
  blockId,
  blockName,
  state,
  hasAthleteWorkouts,
}: {
  blockId: string;
  blockName: string;
  state: PublishState;
  hasAthleteWorkouts: boolean;
}) {
  const mutation = useMutation();
  const { toast } = useToast();
  const { t } = useTranslation();

  return (
    <DisabledWithTooltip
      disabled={!hasAthleteWorkouts}
      reason={t('publishNoAthletesWarning')}
    >
      <Button
        disabled={isPublishActionDisabled(state)}
        onClick={() => {
          // A stalled or send_failed retry needs no extra task cleanup: the
          // publish mutation itself replaces any existing task row (the
          // trigger deletes it before inserting a fresh pending one).
          trackEvent('Strength | Block Builder | Publish Workout', {
            blockId,
          });
          mutation.mutate(
            {
              module: BlockDraftAsyncDomain.Module,
              type: 'Publish',
              input: {
                blockId,
              },
            },
            {
              onSuccess: () => {
                toast({
                  description: t('publishInitiated', { blockName }),
                  severity: 'success',
                });
              },
            },
          );
        }}
      >
        {t('publish')}
      </Button>
    </DisabledWithTooltip>
  );
}

type OnSelectWorkoutError = (args: {
  blockCycleId: number;
  blockSessionId: number;
}) => void;

function BlockPublishErrorBadge({
  workoutIdsWithErrors,
  onSelectError,
}: {
  workoutIdsWithErrors: WorkoutIdWithCycleAndSession[];
  onSelectError: OnSelectWorkoutError;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const publishBlockMenuOpen = Boolean(anchorEl);
  const handleReviewPublishErrors = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClosePublishErrorMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {!!workoutIdsWithErrors.length && (
        <ErrorBadge
          message={t('publishErrorCount', {
            count: workoutIdsWithErrors.length,
          })}
          buttonLabel={t('review')}
          open={publishBlockMenuOpen}
          onClick={handleReviewPublishErrors}
        >
          <PublishErrorsDropdownMenuContent
            open={publishBlockMenuOpen}
            anchorEl={anchorEl}
            onClose={handleClosePublishErrorMenu}
            workoutIdsWithErrors={workoutIdsWithErrors}
            onSelectError={onSelectError}
          />
        </ErrorBadge>
      )}
      {/* {!!amsWorkoutsWithErrors.length && (
        <ErrorBadge
          message={t('amsSyncError')}
          buttonLabel={t('resync')}
          onClick={handleAmsResync}
        />
      )} */}
    </>
  );
}

const ErrorBadge = ({
  message,
  buttonLabel,
  open,
  children,
  onClick,
}: {
  message: string;
  buttonLabel: string;
  open?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
}) => {
  const {
    palette: { error },
  } = useTheme();
  return (
    <div className="flex flex-row justify-center rounded-md items-center p-2 px-4 bg-red-50">
      <MdError size={18} color={error.main} />
      <Typography variant="body2Semibold" className="px-2 text-nowrap">
        {message}
      </Typography>
      <Button
        className="text-red-500 m-0 p-0 ml-2 font-semibold text-sm h-auto"
        color="error"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={onClick}
        variant="text"
      >
        <Typography variant="subtitle1Semibold" className="p-0 m-0">
          {buttonLabel}
        </Typography>
      </Button>
      {children}
    </div>
  );
};

const PublishErrorsDropdownMenuContent = ({
  open,
  anchorEl,
  onClose,
  onSelectError,
  workoutIdsWithErrors,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  workoutIdsWithErrors: WorkoutIdWithCycleAndSession[];
  onSelectError: OnSelectWorkoutError;
}) => {
  const { t } = useTranslation();
  const sortedWorkoutsWithErrors = workoutIdsWithErrors.sort((a, b) => {
    const aCycleSort = a.cycle.startDate;
    const bCycleSort = b.cycle.startDate;
    if (aCycleSort === bCycleSort) {
      return a.session.id - b.session.id;
    } else if (aCycleSort > bCycleSort) {
      return 1;
    } else {
      return -1;
    }
  });

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      {sortedWorkoutsWithErrors.map((workout) => {
        return (
          <MenuItem
            key={workout.workoutId}
            onClick={() => {
              onSelectError({
                blockCycleId: workout.cycle.id,
                blockSessionId: workout.session.id,
              });
              onClose();
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <p>
              {workout.cycle.name}, {workout.session.name}
            </p>
            <p className="text-xs text-gray-400">
              {isoValueToDisplay(workout.assignedDate, t('noDate'))}
            </p>
          </MenuItem>
        );
      })}
    </Menu>
  );
};
